import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { setMemberSession } from "../../hooks/useMemberAuth";
import { socialApi, formatApiError } from "../../lib/authApi";

/**
 * VerifyInboxCard — reusable "Check your email" confirmation screen with
 * 6-digit OTP fallback + resend cooldown + "wrong email?" reset.
 *
 * Two verification paths are always presented:
 *   1) Click the sign-in link inside the email (opens
 *      /auth/magic-link/consume?token=…)
 *   2) Type the 6-digit code below (this component's OTP boxes)
 *
 * The parent (SocialButtons / LoginPage / SignupPage) passes:
 *   • email          — where the code was sent
 *   • rememberMe     — the "trust this device" opt-in from the previous
 *                       step (used only for resends)
 *   • cooldownSeconds — server-supplied cooldown between resend calls
 *   • onSuccess({session_token, member_id, mode, needs_profile_completion})
 *                     — called when the user's OTP is accepted
 *   • onWrongEmail() — called when the user wants to change email
 */
export default function VerifyInboxCard({
  email,
  rememberMe = false,
  cooldownSeconds = 60,
  onSuccess,
  onWrongEmail,
}) {
  const [digits, setDigits] = useState(["", "", "", "", "", ""]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [cooldown, setCooldown] = useState(cooldownSeconds);
  const [resendBusy, setResendBusy] = useState(false);
  const [resendCount, setResendCount] = useState(0);
  const inputsRef = useRef([]);

  // Countdown ticker
  useEffect(() => {
    if (cooldown <= 0) return undefined;
    const t = setTimeout(() => setCooldown((c) => Math.max(0, c - 1)), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  // Auto-focus first empty box on mount / reset
  useEffect(() => {
    const idx = digits.findIndex((d) => !d);
    const target = idx === -1 ? 5 : idx;
    inputsRef.current[target]?.focus();
  }, []);

  const setDigit = (idx, value) => {
    const clean = value.replace(/\D/g, "").slice(0, 1);
    const next = digits.slice();
    next[idx] = clean;
    setDigits(next);
    setErr(null);
    if (clean && idx < 5) {
      inputsRef.current[idx + 1]?.focus();
    }
    // Auto-submit when all 6 filled
    if (clean && idx === 5 && next.every((d) => d)) {
      submit(next.join(""));
    }
  };

  const onKeyDown = (idx, e) => {
    if (e.key === "Backspace" && !digits[idx] && idx > 0) {
      inputsRef.current[idx - 1]?.focus();
    } else if (e.key === "ArrowLeft" && idx > 0) {
      inputsRef.current[idx - 1]?.focus();
    } else if (e.key === "ArrowRight" && idx < 5) {
      inputsRef.current[idx + 1]?.focus();
    }
  };

  const onPaste = (e) => {
    const pasted = (e.clipboardData.getData("text") || "").replace(/\D/g, "").slice(0, 6);
    if (pasted.length === 0) return;
    e.preventDefault();
    const next = pasted.split("").concat(["", "", "", "", "", ""]).slice(0, 6);
    setDigits(next);
    setErr(null);
    if (next.every((d) => d)) submit(next.join(""));
    else inputsRef.current[Math.min(pasted.length, 5)]?.focus();
  };

  const submit = async (codeOverride) => {
    const code = (codeOverride ?? digits.join("")).trim();
    if (code.length !== 6) {
      setErr("Enter all 6 digits.");
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      const { data } = await socialApi.verifyMagicLinkCode(email, code);
      if (data?.session_token) {
        await setMemberSession(data.session_token);
      }
      onSuccess?.(data);
    } catch (e) {
      const msg = formatApiError(e?.response?.data?.detail) || "Verification failed. Try again.";
      setErr(msg);
      // Clear the boxes and refocus so they can try again
      setDigits(["", "", "", "", "", ""]);
      inputsRef.current[0]?.focus();
    } finally {
      setBusy(false);
    }
  };

  const resend = async () => {
    if (cooldown > 0 || resendBusy) return;
    setResendBusy(true);
    setErr(null);
    try {
      const { data } = await socialApi.requestMagicLink(email, rememberMe);
      if (data?.throttled) {
        setCooldown(data.retry_after || cooldownSeconds);
        toast(`Wait ${data.retry_after || cooldownSeconds}s before requesting another code.`);
      } else {
        setCooldown(data?.cooldown_seconds || cooldownSeconds);
        setResendCount((c) => c + 1);
        toast.success(`New code sent to ${email}.`);
      }
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Could not resend code.");
    } finally {
      setResendBusy(false);
    }
  };

  return (
    <div data-testid="verify-inbox-card" className="border p-4 sm:p-5"
         style={{ borderColor: "#454038AA", background: "#0a0907" }}>
      <div className="font-mono text-[10px] uppercase tracking-[0.4em] mb-2"
           style={{ color: "#5DD4B3" }}>
        // TRANSMISSION SENT · AWAITING CLEARANCE
      </div>
      <h2 className="font-display text-2xl sm:text-3xl leading-tight mb-2"
          style={{ color: "#E4DFD3" }}>
        Check your email
      </h2>
      <p className="text-[13px] leading-relaxed mb-4" style={{ color: "#A39E93" }}>
        Your Archive access code was sent to{" "}
        <strong style={{ color: "#E4DFD3" }} data-testid="verify-inbox-email">
          {email}
        </strong>
        . Click the sign-in link inside, or type the 6-digit code below.
      </p>

      {/* OTP boxes */}
      <div className="mb-2">
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] mb-2"
             style={{ color: "#6F6A60" }}>
          6-DIGIT ACCESS CODE
        </div>
        <div className="flex items-center gap-1 sm:gap-2"
             onPaste={onPaste}>
          {digits.map((d, i) => (
            <React.Fragment key={i}>
              <input
                ref={(el) => (inputsRef.current[i] = el)}
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                pattern="[0-9]*"
                maxLength={1}
                value={d}
                onChange={(e) => setDigit(i, e.target.value)}
                onKeyDown={(e) => onKeyDown(i, e)}
                data-testid={`verify-inbox-digit-${i}`}
                aria-label={`Digit ${i + 1}`}
                disabled={busy}
                className="flex-1 min-w-0 w-8 h-12 sm:w-11 sm:h-14 text-center font-mono text-lg sm:text-2xl bg-[#08070A] border focus:outline-none focus:border-[#D92D20] disabled:opacity-60"
                style={{
                  color: "#E0A526",
                  borderColor: err ? "#D92D20" : "#454038",
                  letterSpacing: "0.1em",
                }}
              />
              {i === 2 && (
                <span aria-hidden style={{ color: "#454038" }} className="mx-0 sm:mx-0.5 shrink-0">·</span>
              )}
            </React.Fragment>
          ))}
        </div>
      </div>

      {err && (
        <div className="mb-3 mt-1 font-mono text-[11px]"
             style={{ color: "#D92D20" }}
             data-testid="verify-inbox-error"
             role="alert">
          {err}
        </div>
      )}

      <button
        type="button"
        onClick={() => submit()}
        disabled={busy || digits.some((d) => !d)}
        data-testid="verify-inbox-submit"
        className="mt-2 w-full font-mono text-[11px] uppercase tracking-[0.35em] py-2.5 disabled:opacity-40 transition-colors"
        style={{
          background: "#D92D20",
          color: "#FFFFFF",
          border: "1px solid #D92D20",
        }}
      >
        {busy ? "VERIFYING…" : "VERIFY & ENTER ARCHIVE"}
      </button>

      {/* Resend + wrong-email row */}
      <div className="mt-4 flex items-center gap-3 flex-wrap justify-between">
        <button
          type="button"
          onClick={resend}
          disabled={cooldown > 0 || resendBusy}
          data-testid="verify-inbox-resend"
          className="font-mono text-[10px] uppercase tracking-[0.3em] underline-offset-4 hover:underline disabled:opacity-40"
          style={{ color: cooldown > 0 ? "#6F6A60" : "#5DD4B3" }}
        >
          {resendBusy
            ? "SENDING…"
            : cooldown > 0
              ? `RESEND CODE (${cooldown}S)`
              : "RESEND CODE"}
        </button>
        <button
          type="button"
          onClick={onWrongEmail}
          data-testid="verify-inbox-wrong-email"
          className="font-mono text-[10px] uppercase tracking-[0.3em] underline-offset-4 hover:underline"
          style={{ color: "#A39E93" }}
        >
          WRONG EMAIL?
        </button>
      </div>

      {/* Spam / troubleshooting help */}
      <details className="mt-4 font-mono text-[10px]"
               data-testid="verify-inbox-help"
               style={{ color: "#6F6A60" }}>
        <summary className="cursor-pointer hover:text-[#A39E93] uppercase tracking-[0.25em]">
          Didn&apos;t get the email?
        </summary>
        <ul className="mt-2 ml-2 space-y-1 leading-relaxed" style={{ color: "#8A8477" }}>
          <li>• Check <strong style={{ color: "#A39E93" }}>Spam</strong> or{" "}
              <strong style={{ color: "#A39E93" }}>Promotions</strong> folder.</li>
          <li>• Add <span style={{ color: "#E4DFD3" }}>noreply@thearchive.report</span> to your contacts.</li>
          <li>• The link and code both expire in 20 minutes.</li>
          <li>• Delivery can take up to a minute during high demand.</li>
          {resendCount > 1 && (
            <li>• Only the <strong style={{ color: "#E0A526" }}>most recent</strong> code works — older ones are invalidated.</li>
          )}
        </ul>
      </details>
    </div>
  );
}
